export const CROWD_ROLES = new Set([
  'crowd_analyst',
  'command_supervisor',
  'system_admin',
]);

export const BORDER_ROLES = new Set([
  'border_ops_analyst',
  'intelligence_analyst',
  'border_command_supervisor',
  'border_system_admin',
]);

export const CI_ROLES = new Set([
  'security_officer',
  'facility_security_manager',
  'national_command_supervisor',
  'system_administrator',
]);

export const FORENSICS_ROLES = new Set([
  'investigator',
  'senior_investigator',
  'forensic_analyst',
  'forensics_supervisor',
  'forensics_admin',
]);

export const INTEL_ROLES = new Set([
  'intelligence_analyst',
  'senior_intelligence_officer',
  'intel_command_supervisor',
  'intel_system_admin',
]);

export type VigiloAppId = 'crowd' | 'border' | 'ci' | 'forensics' | 'intel';

type UserLike = {
  role?: string | null;
  orgId?: string | null;
};

export function appForRole(role?: string | null): VigiloAppId | null {
  if (!role) return null;
  if (FORENSICS_ROLES.has(role)) return 'forensics';
  if (INTEL_ROLES.has(role)) return 'intel';
  if (CI_ROLES.has(role)) return 'ci';
  if (BORDER_ROLES.has(role)) return 'border';
  if (CROWD_ROLES.has(role)) return 'crowd';
  return null;
}

export function appForUser(user?: UserLike | null, requestedApp?: VigiloAppId | null): VigiloAppId | null {
  const role = user?.role ?? null;
  if (!role) return null;

  if (role === 'intelligence_analyst') {
    if (requestedApp === 'border' && user?.orgId !== 'vigilo-intel') return 'border';
    if (requestedApp === 'intel' && user?.orgId === 'vigilo-intel') return 'intel';
    return user?.orgId === 'vigilo-intel' ? 'intel' : 'border';
  }

  if (requestedApp && rolesForApp(requestedApp).has(role)) {
    return requestedApp;
  }

  return appForRole(role);
}

function rolesForApp(app: VigiloAppId): Set<string> {
  if (app === 'forensics') return FORENSICS_ROLES;
  if (app === 'intel') return INTEL_ROLES;
  if (app === 'ci') return CI_ROLES;
  if (app === 'border') return BORDER_ROLES;
  return CROWD_ROLES;
}

export function pathForApp(app: VigiloAppId): string {
  if (app === 'forensics') return '/forensics/dashboard';
  if (app === 'intel') return '/intel/dashboard';
  if (app === 'ci') return '/ci/map';
  if (app === 'border') return '/border/map';
  return '/operations';
}

export function pathForUser(user?: UserLike | null, requestedApp?: VigiloAppId | null): string {
  const app = appForUser(user, requestedApp);
  if (!app) return '/login';
  if (app === 'intel' && user?.role === 'intel_system_admin') return '/intel/admin/users';
  return pathForApp(app);
}

export function loginPath(app: VigiloAppId, reason?: 'unauthorized'): string {
  return `/login?app=${app}${reason ? `&reason=${reason}` : ''}`;
}
